import React, { FC, useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import debounce from 'lodash.debounce';
import { adminOrganizationSearchTemplate } from 'apollo/templates/orders';
import { AdminOrderOrganization } from 'models/admin-order';

type Props = {
    label: string;
    value: AdminOrderOrganization | null;
    onChange: (organization: AdminOrderOrganization | null) => void;
    /** clinic or lab; omit for both. */
    type?: string;
    dataTest?: string;
};

/** Organization picker that searches by name on the server (two characters or more). */
const OrganizationPicker: FC<Props> = ({ label, value, onChange, type, dataTest }) => {
    const [inputValue, setInputValue] = useState('');
    const [search, { data, loading }] = useLazyQuery<{ adminOrganizationSearch: AdminOrderOrganization[] }>(
        adminOrganizationSearchTemplate,
        { fetchPolicy: 'network-only' },
    );
    const [debouncedSearch] = useState(() =>
        debounce((text: string) => search({ variables: { search: text, type, limit: 20 } }), 300),
    );

    useEffect(() => {
        if (inputValue.trim().length >= 2 && inputValue !== value?.name) {
            debouncedSearch(inputValue.trim());
        }
    }, [inputValue]);

    const options = data?.adminOrganizationSearch ?? [];

    return (
        <Autocomplete
            value={value}
            options={value && !options.some((option) => option._id === value._id) ? [value, ...options] : options}
            getOptionLabel={(option) => option.name ?? option._id}
            isOptionEqualToValue={(option, selected) => option._id === selected._id}
            filterOptions={(all) => all}
            loading={loading}
            onChange={(_, organization) => onChange(organization)}
            inputValue={inputValue}
            onInputChange={(_, text) => setInputValue(text)}
            renderOption={(props, option) => (
                <li {...props} key={option._id}>
                    <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {[option.type, option.organizationNumber, option.city].filter(Boolean).join(' · ')}
                        </Typography>
                    </Box>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    label={label}
                    data-test={dataTest}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default OrganizationPicker;
